<script setup>
/**
 * @fileoverview ConsoleSessionView - per-session viewer
 *
 * Renders the toolbar + Guacamole display for a single session whose lifetime
 * is owned by the consoleSessions store. Lab sessions use `vmId` as the store
 * key; share portal sessions use `sessionKey` (`share:<shareId>:<vmId>`).
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Loader2, AlertCircle, RefreshCw } from 'lucide-vue-next'
import ConsoleToolbar from './ConsoleToolbar.vue'
import { useConsoleSessionsStore } from '@/stores/consoleSessions'
import { getLabVMs } from '@/api/labs'
import { getShareVMs } from '@/api/sharePortal'

const props = defineProps({
  vmId: {
    type: String,
    required: true
  },
  /** Store map key; defaults to `vmId` for lab consoles */
  sessionKey: {
    type: String,
    default: ''
  }
})

const sessionsStore = useConsoleSessionsStore()

const storeKey = computed(() => props.sessionKey || props.vmId)

const session = computed(() => sessionsStore.sessions.get(storeKey.value) || null)
const connectionState = computed(() => session.value?.controller.connectionState.value || 'idle')
const errorMessage = computed(() => session.value?.controller.errorMessage.value || null)
const reconnectAttempts = computed(
  () => session.value?.controller.reconnectAttempts.value || 0
)
const reconnectDelay = computed(() => session.value?.controller.reconnectDelay.value || 0)

const isReconnectExceeded = computed(
  () => connectionState.value === 'error' && reconnectAttempts.value >= 5
)

const containerRef = ref(null)
const displayRef = ref(null)
const isFullscreen = ref(false)
const availableVMs = ref([])

const backHrefForToolbar = computed(() => {
  const s = session.value
  if (!s) return ''
  if (s.shareId) {
    return `/share/${s.shareId}/vms`
  }
  if (s.labId && !s.shareId) {
    return `/labs/${s.labId}`
  }
  return ''
})

async function ensureConnection() {
  if (!session.value || !displayRef.value) return
  const rect = displayRef.value.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) {
    await new Promise(resolve => setTimeout(resolve, 50))
  }
  sessionsStore.registerDisplay(storeKey.value, displayRef.value)
  if (connectionState.value === 'idle') {
    try {
      await sessionsStore.connectSession(storeKey.value)
    } catch (err) {
      console.error('[ConsoleSessionView] connect failed:', err)
    }
  } else if (connectionState.value === 'connected') {
    session.value.controller.scaleDisplayToFit()
  }
}

watch(
  () => sessionsStore.activeVmId,
  async newKey => {
    if (newKey === storeKey.value) {
      await nextTick()
      session.value?.controller.scaleDisplayToFit()
    }
  }
)

async function loadAvailableVMs() {
  const s = session.value
  if (!s) return
  try {
    if (s.shareId) {
      const vms = await getShareVMs(s.shareId)
      availableVMs.value = Array.isArray(vms) ? vms : vms?.data || []
    } else if (s.labId) {
      const vms = await getLabVMs(s.labId)
      availableVMs.value = Array.isArray(vms) ? vms : vms?.data || []
    }
  } catch (err) {
    console.warn('[ConsoleSessionView] failed to load VMs:', err)
  }
}

onMounted(async () => {
  await nextTick()
  await ensureConnection()
  await loadAvailableVMs()
  document.addEventListener('fullscreenchange', handleFullscreenChange)
})

onBeforeUnmount(() => {
  document.removeEventListener('fullscreenchange', handleFullscreenChange)
})

function getStateText() {
  if (connectionState.value === 'reconnecting') {
    return `Reconnecting (attempt ${reconnectAttempts.value}/5)...`
  }
  const states = {
    idle: 'Initializing...',
    connecting: 'Connecting to console...',
    connected: 'Connected',
    disconnected: 'Disconnected',
    error: 'Connection Error'
  }
  return states[connectionState.value] || 'Unknown'
}

async function toggleFullscreen() {
  try {
    if (!isFullscreen.value) {
      if (containerRef.value) {
        await containerRef.value.requestFullscreen()
        isFullscreen.value = true
      }
    } else if (document.fullscreenElement) {
      await document.exitFullscreen()
      isFullscreen.value = false
    }
  } catch (err) {
    console.error('Fullscreen toggle failed:', err)
  }
}

function handleFullscreenChange() {
  isFullscreen.value = !!document.fullscreenElement
}

function handleMinimize() {
  sessionsStore.minimizeSession(storeKey.value)
}

function handleSendClipboard(text) {
  session.value?.controller.sendClipboard(text)
}

function handleSendCtrlAltDel() {
  session.value?.controller.sendCtrlAltDel()
}

function handleDialogOpen() {
  session.value?.controller.setKeyboardEnabled(false)
}

function handleDialogClose() {
  session.value?.controller.setKeyboardEnabled(true)
}

function handleOpenVM(otherVmId) {
  const s = session.value
  if (!s) return
  const target = availableVMs.value.find(v => v.id === otherVmId)
  if (s.shareId) {
    const result = sessionsStore.openShareSession({
      shareId: s.shareId,
      vmId: otherVmId,
      vmName: target?.name || otherVmId,
      labName: s.labName
    })
    if (result.ok) {
      sessionsStore.focusSession(result.session.sessionKey)
    }
  } else {
    const result = sessionsStore.openSession({
      labId: s.labId,
      vmId: otherVmId,
      vmName: target?.name || otherVmId,
      labName: s.labName
    })
    if (result.ok) {
      sessionsStore.focusSession(result.session.sessionKey)
    }
  }
}

function handleCloseSession() {
  sessionsStore.closeSession(storeKey.value)
}

function handleRetry() {
  session.value?.controller.reconnect()
}
</script>

<template>
  <div
    ref="containerRef"
    class="console-session-view flex h-full w-full flex-col overflow-hidden bg-gray-50 dark:bg-slate-900"
    :class="{ 'fullscreen-active': isFullscreen }"
  >
    <ConsoleToolbar
      :vm-name="session?.vmName || 'VM Console'"
      :back-href="backHrefForToolbar"
      :lab-id="session?.labId || ''"
      :session-key="storeKey"
      :current-vm-id="vmId"
      show-close-session
      :connection-state="connectionState"
      :reconnect-attempts="reconnectAttempts"
      :available-v-ms="availableVMs"
      @minimize="handleMinimize"
      @close-session="handleCloseSession"
      @fullscreen="toggleFullscreen"
      @send-clipboard="handleSendClipboard"
      @send-ctrl-alt-del="handleSendCtrlAltDel"
      @dialog-open="handleDialogOpen"
      @dialog-close="handleDialogClose"
      @open-v-m="handleOpenVM"
    />

    <div class="relative flex-1 overflow-hidden bg-black">
      <div
        v-if="connectionState === 'idle' || connectionState === 'connecting'"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center bg-gray-100 text-gray-900 dark:bg-gray-900 dark:text-white"
      >
        <Loader2 class="mb-4 h-12 w-12 animate-spin text-indigo-500" />
        <p class="text-lg font-medium">{{ getStateText() }}</p>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Please wait...</p>
      </div>

      <div
        v-else-if="connectionState === 'reconnecting'"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center bg-gray-100/95 text-gray-900 dark:bg-gray-900/95 dark:text-white"
      >
        <RefreshCw class="mb-4 h-12 w-12 animate-spin text-amber-500" />
        <p class="text-lg font-medium">{{ getStateText() }}</p>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Next attempt in {{ Math.ceil(reconnectDelay / 1000) }}s...
        </p>
      </div>

      <div
        v-else-if="isReconnectExceeded"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center bg-gray-100 p-8 text-gray-900 dark:bg-gray-900 dark:text-white"
      >
        <AlertCircle class="mb-4 h-16 w-16 text-red-500" />
        <h3 class="mb-2 text-xl font-semibold">Connection Lost</h3>
        <p class="mb-4 max-w-md text-center text-gray-600 dark:text-gray-300">
          {{ errorMessage || 'Unable to establish console connection after multiple attempts' }}
        </p>
        <button
          @click="handleRetry"
          class="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-2 font-medium transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          aria-label="Retry connection"
        >
          <RefreshCw class="h-4 w-4" />
          Retry Connection
        </button>
      </div>

      <div
        v-else-if="connectionState === 'error' && !isReconnectExceeded"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center bg-gray-100 p-8 text-gray-900 dark:bg-gray-900 dark:text-white"
      >
        <AlertCircle class="mb-4 h-16 w-16 text-red-500" />
        <h3 class="mb-2 text-xl font-semibold">Connection Failed</h3>
        <p class="mb-4 max-w-md text-center text-gray-600 dark:text-gray-300">
          {{ errorMessage || 'Unable to establish console connection' }}
        </p>
        <button
          @click="handleRetry"
          class="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-2 font-medium transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          aria-label="Retry connection"
        >
          <RefreshCw class="h-4 w-4" />
          Retry Connection
        </button>
      </div>

      <div
        ref="displayRef"
        class="guacamole-display absolute inset-0 flex items-center justify-center"
        :class="{ 'opacity-0': connectionState !== 'connected' }"
      >
        <!-- Guacamole canvas will be appended here by the controller -->
      </div>
    </div>
  </div>
</template>

<style scoped>
.console-session-view.fullscreen-active {
  width: 100vw;
  height: 100vh;
}
.guacamole-display {
  transition: opacity 0.3s ease-in-out;
  overflow: hidden;
}
.guacamole-display :deep(canvas) {
  display: block;
  image-rendering: pixelated;
  image-rendering: crisp-edges;
}
</style>
